import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { applyDetectionLimit, formatWithDetectionLimit, getElementUnit } from '../../config/detection-limits.config';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  IconButton,
  Tabs,
  Tab,
  LinearProgress,
  useTheme,
  alpha,
  Stack,
  AccordionSummary,
  AccordionDetails,
  Accordion,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Snackbar,
  Tooltip,
  Switch,
  Collapse,
  TablePagination,
  InputAdornment,
} from '@mui/material';
import {
  Science as ScienceIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon,
  Analytics as AnalyticsIcon,
  Assessment as AssessmentIcon,
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  BugReport as BugReportIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Archive as ArchiveIcon,
  Settings,
  BarChart,
  VerifiedUser,
  Add,
  Edit,
  Delete,
  FolderOpen,
  FileUpload,
  Search,
  Clear,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import samplesService, { Sample, Priority, SampleStatus } from '../../services/samplesService';
import apiService from '../../services/api';
import api from '../../services/api';

// 🚀 PERFORMANCE OPTIMIZATION: Debounce hook for input optimization
import { useCallback as useDebounceCallback, useMemo as useDebounce } from 'react';

// Major elements (and their oxide forms) are reported in % in the lot modal,
// because fusion-CSV ICP runs and BGM/ZMSM workflows calibrate against oxide wt%
// rather than ppm. Trace elements stay in ppm. Keep this list in sync with the
// backend MAJOR_ELEMENT_SYMBOLS / MAJOR_OXIDE_ELEMENTS in icp-import.controller.ts.
const MAJOR_ELEMENT_DISPLAY_PERCENT = new Set<string>([
  'Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti',
  'Al2O3', 'CaO', 'Fe2O3', 'K2O', 'MgO', 'MnO', 'Na2O', 'P2O5', 'SiO2', 'TiO2',
]);
const isPercentDisplayElement = (el: string): boolean =>
  MAJOR_ELEMENT_DISPLAY_PERCENT.has(el);

// Enhanced interfaces for comprehensive ICP analysis matching SAA
interface PreparationData {
  initial_weight: number;
  digestion_method: string;
  initial_volume: number;
  dilutions: Array<{
    volumeTaken: number;
    finalVolume: number;
    dilutionFactor: number;
  }>;
  total_dilution_factor: number;
  dilution_factor?: number; // Alternative name for compatibility
  preparation_completed_at: string;
  technician_id: string;
  technician_name: string;
}

interface CalibrationStandard {
  id: string;
  element: string;
  concentration_ppm: number;
  intensity: number;
  used_at: string;
}

interface QualityControl {
  id: string;
  type: 'BLANK' | 'STANDARD' | 'DUPLICATE' | 'SPIKE';
  element: string;
  expected_value?: number;
  measured_value: number;
  intensity: number;
  recovery_percent?: number;
  within_limits: boolean;
  timestamp: string;
}

interface AnalysisReading {
  id: string;
  element: string;
  intensity: number;
  absorbance?: number; // Optional for compatibility with SAA
  concentration_ppm: number;
  concentration_final_percent: number;
  uncertainty: number;
  detection_limit: number;
  timestamp: string;
  instrument_settings: {
    wavelength: number;
    rf_power: number;
    nebulizer_flow: number;
    integration_time: number;
  };
}

interface InstrumentStatus {
  last_calibration: string;
  r_squared: number;
  plasma_stable: boolean;
  torch_hours: number;
  nebulizer_flow_stable: boolean;
  status: 'READY' | 'CALIBRATING' | 'ANALYZING' | 'MAINTENANCE';
  last_qc_check: string;
  temperature: number;
  humidity: number;
}

interface ICPAnalysisForm {
  sample_id: string;
  preparation_data: PreparationData | null;
  calibration_standards: Array<{
    id: string;
    element: string;
    concentration_ppm: number;
    intensity: number;
    used_at: string;
  }>;
  quality_controls: Array<{
    id: string;
    element: string;
    expected_concentration: number;
    measured_concentration: number;
    recovery_percent: number;
    used_at: string;
  }>;
  analysis_readings: AnalysisReading[];
  instrument_status: InstrumentStatus;
  analysis_started_at: string | null;
  analysis_completed_at: string | null;
  analyst_id: string;
  notes: string;
  status: 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED';
  analysis_type?: ICPAnalysisType;
  digestion_method?: string;
}

interface MethodValidation {
  element: string;
  wavelength: number;
  lod_ppm: number;
  loq_ppm: number;
  accuracy_percent: number;
  precision_rsd_percent: number;
  expanded_uncertainty_percent: number;
}

type ICPAnalysisType = '4-acides' | 'fusion' | 'unknown';

interface ICPImportResult {
  sample_reference: string;
  element: string;
  concentration_ppm: number;
  concentration_percent: number;
  flags?: string;
  timestamp: string;
  unit?: 'ppm' | '%';
  analysisType?: ICPAnalysisType;
}

interface ICPFileInfo {
  filename: string;
  path: string;
  size: number;
  modified: string;
  matchedSamples: string[];
  analysisType?: ICPAnalysisType;
}

// 🚀 PERFORMANCE: Memoized input component to prevent re-renders of 460+ input fields
const MemoizedTextField = memo<any>(({ value, onChange, ...props }) => (
  <TextField value={value} onChange={onChange} {...props} />
));

// 🚀 PERFORMANCE OPTIMIZATION: Memoized component to prevent unnecessary re-renders
const ICPAnalystDashboard: React.FC = memo(() => {
  const { user } = useAuth();
  const theme = useTheme();
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [analysisDialog, setAnalysisDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [methodValidations, setMethodValidations] = useState<MethodValidation[]>([]);
  const [instrumentStatus, setInstrumentStatus] = useState<InstrumentStatus>({
    last_calibration: '',
    r_squared: 0,
    plasma_stable: false,
    torch_hours: 0,
    nebulizer_flow_stable: false,
    status: 'READY',
    last_qc_check: '',
    temperature: 0,
    humidity: 0,
  });

  const [analysisData, setAnalysisData] = useState<ICPAnalysisForm>({
    sample_id: '',
    preparation_data: null,
    calibration_standards: [],
    quality_controls: [],
    analysis_readings: [],
    instrument_status: instrumentStatus,
    analysis_started_at: null,
    analysis_completed_at: null,
    analyst_id: '',
    notes: '',
    status: 'DRAFT',
  });

  const [stats, setStats] = useState({
    ready_for_analysis: 0,
    in_progress: 0,
    completed_today: 0,
    urgent_samples: 0,
    total_elements_analyzed: 0,
    average_rsd: 0,
  });

  // 🚀 PAGINATION & SEARCH STATE
  const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(250); // ~10 batches worth
  const [totalSamples, setTotalSamples] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [batchCount, setBatchCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Batch analysis state
  const [selectedBatch, setSelectedBatch] = useState<{batchNumber: string, samples: Sample[]} | null>(null);
  const [batchAnalysisDialog, setBatchAnalysisDialog] = useState(false);
  const [batchAnalysisData, setBatchAnalysisData] = useState<Record<string, Record<string, { ppm: number | null; percent?: number; timestamp: string }>>>({});
  // ✅ REMOVED: batchInitializing state - matches AU pattern (no async initialization)
  const [sendingToPrepBatch, setSendingToPrepBatch] = useState(false); // 🔥 NEW: Send to prep loading state
  
  // ICP File Import State
  const [importLoading, setImportLoading] = useState(false);
  const [availableFiles, setAvailableFiles] = useState<ICPFileInfo[]>([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importedFileName, setImportedFileName] = useState<string | null>(null);
  const [selectedFilesForImport, setSelectedFilesForImport] = useState<Set<string>>(new Set());
  const [currentBatchAnalysisType, setCurrentBatchAnalysisType] = useState<ICPAnalysisType | null>(null);
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // 🚀 PROFESSIONAL LIMS SOLUTION: Native input performance + full visibility
  const [useNativeInputs, setUseNativeInputs] = useState(true); // Always use native inputs
  const [virtualScrollEnabled, setVirtualScrollEnabled] = useState(true); // Enable virtualization for large batches
  
  // 🚀 PERFORMANCE: Virtualization state for professional laboratory interface
  const [visibleRowStart, setVisibleRowStart] = useState(0);
  const [visibleRowCount] = useState(8); // Show 8 rows at a time for optimal performance
  const [containerHeight] = useState(500); // Fixed container height for virtual scrolling
  const [rowHeight] = useState(60); // Fixed row height for calculations

  // 🚀 PERFORMANCE: Virtual rendering state for massive sample batches
  const [maxVisibleSamples, setMaxVisibleSamples] = useState(6); // Start with 6 samples max
  const [isLoadingMoreSamples, setIsLoadingMoreSamples] = useState(false);
  const [showAllSamples, setShowAllSamples] = useState(false);

  // Complete ICP elements list (40 elements) with updated wavelengths
  const icpElements = [
    // Major elements
    { symbol: 'Al', name: 'Aluminium', wavelength: 309.271, typical_range: '0.1-500 ppm' },
    { symbol: 'Ca', name: 'Calcium', wavelength: 317.933, typical_range: '0.1-200 ppm' },
    { symbol: 'Fe', name: 'Fer', wavelength: 259.940, typical_range: '0.1-100 ppm' },
    { symbol: 'K', name: 'Potassium', wavelength: 766.490, typical_range: '0.1-100 ppm' },
    { symbol: 'Mg', name: 'Magnésium', wavelength: 285.213, typical_range: '0.01-100 ppm' },
    { symbol: 'Mn', name: 'Manganèse', wavelength: 257.610, typical_range: '0.02-100 ppm' },
    { symbol: 'Na', name: 'Sodium', wavelength: 589.592, typical_range: '0.1-100 ppm' },
    { symbol: 'P', name: 'Phosphore', wavelength: 214.914, typical_range: '0.1-100 ppm' },
    { symbol: 'S', name: 'Soufre', wavelength: 180.669, typical_range: '0.1-100 ppm' },
    { symbol: 'Si', name: 'Silicium', wavelength: 251.611, typical_range: '0.1-100 ppm' },
    { symbol: 'Ti', name: 'Titane', wavelength: 334.941, typical_range: '0.1-100 ppm' },
    
    // Trace elements
    { symbol: 'Ag', name: 'Argent', wavelength: 328.068, typical_range: '0.005-50 ppm' },
    { symbol: 'As', name: 'Arsenic', wavelength: 188.979, typical_range: '0.01-50 ppm' },
    { symbol: 'Ba', name: 'Baryum', wavelength: 455.403, typical_range: '0.01-100 ppm' },
    { symbol: 'Be', name: 'Beryllium', wavelength: 313.042, typical_range: '0.005-50 ppm' },
    { symbol: 'Bi', name: 'Bismuth', wavelength: 223.061, typical_range: '0.01-50 ppm' },
    { symbol: 'Cd', name: 'Cadmium', wavelength: 228.802, typical_range: '0.01-50 ppm' },
    { symbol: 'Co', name: 'Cobalt', wavelength: 238.892, typical_range: '0.02-100 ppm' },
    { symbol: 'Cr', name: 'Chrome', wavelength: 267.716, typical_range: '0.01-100 ppm' },
    { symbol: 'Cu', name: 'Cuivre', wavelength: 324.754, typical_range: '0.05-100 ppm' },
    { symbol: 'Ga', name: 'Gallium', wavelength: 294.364, typical_range: '0.01-50 ppm' },
    { symbol: 'Ge', name: 'Germanium', wavelength: 209.426, typical_range: '0.01-50 ppm' },
    { symbol: 'In', name: 'Indium', wavelength: 325.609, typical_range: '0.01-50 ppm' },
    { symbol: 'Li', name: 'Lithium', wavelength: 670.784, typical_range: '0.01-100 ppm' },
    { symbol: 'Mo', name: 'Molybdène', wavelength: 202.030, typical_range: '0.01-100 ppm' },
    { symbol: 'Nb', name: 'Niobium', wavelength: 309.418, typical_range: '0.01-50 ppm' },
    { symbol: 'Ni', name: 'Nickel', wavelength: 231.604, typical_range: '0.04-100 ppm' },
    { symbol: 'Pb', name: 'Plomb', wavelength: 220.353, typical_range: '0.08-100 ppm' },
    { symbol: 'Rb', name: 'Rubidium', wavelength: 794.760, typical_range: '0.01-50 ppm' },
    { symbol: 'Sb', name: 'Antimoine', wavelength: 206.833, typical_range: '0.01-50 ppm' },
    { symbol: 'Sc', name: 'Scandium', wavelength: 361.384, typical_range: '0.01-50 ppm' },
    { symbol: 'Sn', name: 'Étain', wavelength: 189.989, typical_range: '0.01-50 ppm' },
    { symbol: 'Sr', name: 'Strontium', wavelength: 407.771, typical_range: '0.01-100 ppm' },
    { symbol: 'Ta', name: 'Tantale', wavelength: 240.063, typical_range: '0.01-50 ppm' },
    { symbol: 'Te', name: 'Tellure', wavelength: 214.281, typical_range: '0.01-50 ppm' },
    { symbol: 'Th', name: 'Thorium', wavelength: 283.730, typical_range: '0.01-50 ppm' },
    { symbol: 'U', name: 'Uranium', wavelength: 409.014, typical_range: '0.01-50 ppm' },
    { symbol: 'V', name: 'Vanadium', wavelength: 292.402, typical_range: '0.01-100 ppm' },
    { symbol: 'W', name: 'Tungstène', wavelength: 207.912, typical_range: '0.01-50 ppm' },
    { symbol: 'Y', name: 'Yttrium', wavelength: 371.029, typical_range: '0.01-50 ppm' },
    { symbol: 'Zn', name: 'Zinc', wavelength: 213.856, typical_range: '0.03-100 ppm' },
    { symbol: 'Zr', name: 'Zirconium', wavelength: 343.823, typical_range: '0.01-50 ppm' },
  ];

  // Batch analysis common values
  const [batchCommonValues, setBatchCommonValues] = useState({
    calibrationStandard: 'Multi-element Standard 1000 ppm',
    plasmaPower: 1150,
    nebulizer: 'Concentric Glass',
    detectionMethod: 'Inductively Coupled Plasma Optical Emission Spectrometry'
  });

  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());

  // ✅ CRITICAL FIX: Consistent element ordering for the entire batch
  // 🔧 ENHANCED: Now uses UNION of ALL samples in batch to handle per-sample element differences
  const getBatchElementOrder = useCallback((): string[] => {
    // 🔥 CRITICAL FIX: Use UNION of selected_elements from ALL samples in the batch
    if (selectedBatch && selectedBatch.samples.length > 0) {
      const allElements = new Set<string>();
      
      // 🔧 FIX: Iterate over ALL samples to get union of elements
      selectedBatch.samples.forEach((sample) => {
        const selectedElements = sample.metadonnees?.selected_elements || [];
        const elementRouting = sample.metadonnees?.element_lab_routing || {};
        const isMultiLabSample = sample.metadonnees?.multi_lab_workflow === true || 
                                 sample.metadonnees?.bgm_method_selected === true;
        
        if (selectedElements && Array.isArray(selectedElements) && selectedElements.length > 0) {
          // 🔬 MULTI-LAB FIX: Filter out elements NOT routed to ICP
          let icpElements = selectedElements;
          if (isMultiLabSample && Object.keys(elementRouting).length > 0) {
            icpElements = selectedElements.filter(element => {
              const routing = elementRouting[element];
              // Include element if: no routing specified OR routed to ICP
              return !routing || routing === 'LABORATOIRE_ICP';
            });
          }
          icpElements.forEach(el => allElements.add(el));
        }
      });
      
      if (allElements.size > 0) {
        // Sort elements in logical order (major first, then trace)
        const majorElements = ['Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti'];
        const result = Array.from(allElements).sort((a, b) => {
          const aIsMajor = majorElements.includes(a);
          const bIsMajor = majorElements.includes(b);
          if (aIsMajor && !bIsMajor) return -1;
          if (!aIsMajor && bIsMajor) return 1;
          return a.localeCompare(b);
        });
        console.log(`✅ Using ${result.length} ICP elements from all samples (union):`, result);
        return result;
      }
      
      // Fallback to method mapping using first sample
      const firstSample = selectedBatch.samples[0];
      console.log(`⚠️ No selected_elements found, falling back to method mapping`);
      console.log(`🔍 ICP Analyst Debug: analyses_demandees:`, firstSample.metadonnees?.analyses_demandees);
        // 🔥 CRITICAL FIX: Use EXACT same definitions as ElementSelector.tsx to avoid inconsistencies
        // Import the method definitions from ElementSelector instead of duplicating
        const analysesDemandees = firstSample.metadonnees?.analyses_demandees;
        if (analysesDemandees && Array.isArray(analysesDemandees)) {
          // 🔥 CENTRALIZED: Use exact same method definitions as reception
          const methodToElementsMap: Record<string, string[]> = {
            'AA-PRE.MO/ANA/002': ['Ag', 'Cd', 'Co', 'Cu', 'Fe', 'Mn', 'Ni', 'Pb', 'Zn'],
            'D4A-ICP- PRE.MO/ANA/038': [
              'Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 
              'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Ga', 'Ge', 
              'In', 'Li', 'Mo', 'Nb', 'Ni', 'Pb', 'Sb', 'Sn', 'Sr', 
              'Ta', 'Th', 'V', 'W', 'Y', 'Zr',
              // 🔧 CRITICAL FIX: Use elements that ICP machine actually provides
              'B', 'Se', 'Tl', 'Zn'
            ], // 41 elements - CORRECTED to match actual ICP machine output
            'D4A-ICP- PRE.MO/ANA/037': [
              'Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 
              'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Ni', 'Pb', 'Zn'
            ], // 23 elements - EXACT same as ElementSelector.tsx
            'FP-ICP PRE.MO/ANA/007': [
              'Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 
              'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu'
            ], // 21 elements - EXACT same as ElementSelector.tsx
            'BGM-MULTI-LAB': ['Ag', 'As', 'Cu', 'Fe', 'Mo', 'Pb', 'Sn', 'Zn'] // BGM: Only ICP elements (Au goes to AU lab)
          };
          
          const allElements = new Set<string>();
          analysesDemandees.forEach(methodCode => {
            const methodElements = methodToElementsMap[methodCode];
            if (methodElements) {
              methodElements.forEach(element => allElements.add(element));
            }
          });
          
          if (allElements.size > 0) {
            const elementsArray = Array.from(allElements);
            console.log(`⚠️ Using ${elementsArray.length} elements from method fallback:`, elementsArray);
            return elementsArray;
          }
        }
    }
    
    // Ultimate fallback: Return empty array to force explicit element selection
    console.warn('⚠️ No batch or selected_elements found - cannot determine which elements to analyze');
    // Return the most common ICP elements as a safety fallback
    return ['Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 
            'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Ni', 'Pb', 'Zn'];
  }, [selectedBatch]);

  // 🔧 FIX: Get elements for a SPECIFIC sample (for greyed field detection)
  const getSampleSpecificElements = useCallback((sample: Sample): string[] => {
    const selectedElements = sample.metadonnees?.selected_elements || [];
    const elementRouting = sample.metadonnees?.element_lab_routing || {};
    const isMultiLabSample = sample.metadonnees?.multi_lab_workflow === true || 
                             sample.metadonnees?.bgm_method_selected === true;
    
    if (selectedElements && Array.isArray(selectedElements) && selectedElements.length > 0) {
      // 🔬 MULTI-LAB FIX: Filter out elements NOT routed to ICP
      if (isMultiLabSample && Object.keys(elementRouting).length > 0) {
        return selectedElements.filter(element => {
          const routing = elementRouting[element];
          return !routing || routing === 'LABORATOIRE_ICP';
        });
      }
      return selectedElements;
    }
    return getBatchElementOrder(); // Fallback to batch order if no specific elements
  }, [getBatchElementOrder]);

  // PERFORMANCE OPTIMIZATION: Memoize getSampleElements with useCallback
  // Returns batch-wide element order (for column generation)
  const getSampleElements = useCallback((sample: Sample): string[] => {
    // 🔥 CRITICAL FIX: Always use the batch element order to ensure consistent columns
    return getBatchElementOrder();
  }, [getBatchElementOrder]);

  // ✅ FIXED: Synchronous batch initialization (matches AU pattern - no race conditions)
  const initializeBatchAnalysisData = (samples: Sample[]) => {
    const initialData: Record<string, Record<string, { ppm: number; percent?: number; timestamp: string }>> = {};
    const elementsToAnalyze = getBatchElementOrder();
    
    // Initialize with zeros - data will be loaded via CSV import
    samples.forEach((sample) => {
      initialData[sample.id] = {};
      
      // ✅ FIXED: Initialize with 0 instead of null (matches AU, prevents '0' display issue)
      elementsToAnalyze.forEach((element: string) => {
        initialData[sample.id][element] = {
          ppm: 0, // Initialize with 0 (will be overwritten by CSV import)
          percent: 0,
          timestamp: new Date().toISOString()
        };
      });
    });
    
    setBatchAnalysisData(initialData);
  };

  // 🚀 PERFORMANCE: ULTRA-optimized batch data update with direct mutation
  const updateBatchAnalysisData = useCallback((sampleId: string, element: string, value: { ppm: number | null; percent?: number; timestamp: string }) => {
    setBatchAnalysisData(prev => {
      // 🚀 ULTRA-OPTIMIZATION: Skip update if value hasn't changed
      const currentPpm = prev[sampleId]?.[element]?.ppm;
      const currentPct = prev[sampleId]?.[element]?.percent;
      if (currentPpm === value.ppm && currentPct === value.percent) {
        return prev;
      }

      // 🚀 DIRECT MUTATION for maximum speed
      const newData = { ...prev };
      if (!newData[sampleId]) {
        newData[sampleId] = {};
      }
      newData[sampleId] = { ...newData[sampleId], [element]: value };
      return newData;
    });
  }, []);

  // 🚀 PERFORMANCE: Memoized onChange handler to prevent function recreation
  const createOptimizedOnChange = useCallback((sampleId: string, element: string) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(e.target.value) || 0;
      const asPercent = isPercentDisplayElement(element);
      updateBatchAnalysisData(sampleId, element, {
        ppm: asPercent ? value * 10000 : value,
        percent: asPercent ? value : value / 10000,
        timestamp: new Date().toISOString()
      });
    };
  }, [updateBatchAnalysisData]);

  // Helper function to group samples by batch
  const groupSamplesByBatch = (samples: Sample[]) => {
    const batches: Record<string, Sample[]> = {};
    const individualSamples: Sample[] = [];

    samples.forEach(sample => {
      const batchInfo = sample.metadonnees?.batch_info;
      if (batchInfo && batchInfo.is_batch_sample && batchInfo.batch_number) {
        if (!batches[batchInfo.batch_number]) {
          batches[batchInfo.batch_number] = [];
        }
        batches[batchInfo.batch_number].push(sample);
      } else {
        individualSamples.push(sample);
      }
    });

    return { batches, individualSamples };
  };

  const loadICPAnalysisData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 🚀 OPTIMIZED: Use new paginated API endpoint with BGM filtering on backend
      const response = await api.get<{
        samples: Sample[];
        total: number;
        page: number;
        totalPages: number;
        batchCount: number;
      }>('/samples/queues/icp-analysis', {
        params: {
          page: page + 1, // API is 1-indexed, MUI is 0-indexed
          limit: rowsPerPage,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          sortBy: 'date',
          sortOrder: 'desc',
        }
      });
      
      const icpAnalysisSamples = response.samples || [];
      
      setSamples(icpAnalysisSamples);
      setTotalSamples(response.total || 0);
      setTotalPages(response.totalPages || 0);
      setBatchCount(response.batchCount || 0);
      
      // 🚀 PERFORMANCE: Calculate stats from current page data
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      let urgentCount = 0;
      let completedTodayCount = 0;
      let inProgressCount = 0;
      let totalElements = 0;
      let totalRsd = 0;
      let rsdSampleCount = 0;
      
      icpAnalysisSamples.forEach(sample => {
        if (sample.priorite === Priority.URGENTE) urgentCount++;
        
        const completedAt = sample.metadonnees?.icp_analysis_data?.analysis_completed_at;
        if (completedAt && new Date(completedAt) >= today) completedTodayCount++;
        
        const readings = sample.metadonnees?.icp_analysis_data?.analysis_readings || [];
        if (readings.length > 0) {
          inProgressCount++;
          totalElements += readings.length;
          const sampleRsd = readings.reduce((acc: number, r: any) => acc + (r.uncertainty || 0), 0) / readings.length;
          totalRsd += sampleRsd;
          rsdSampleCount++;
        }
      });
      
      setStats({
        ready_for_analysis: response.total || icpAnalysisSamples.length,
        in_progress: inProgressCount,
        completed_today: completedTodayCount,
        urgent_samples: urgentCount,
        total_elements_analyzed: totalElements,
        average_rsd: rsdSampleCount > 0 ? totalRsd / rsdSampleCount : 0,
      });
      
    } catch (error) {
      console.error('Error loading ICP analysis data:', error);
      setError('Erreur lors du chargement des données d\'analyse ICP');
    } finally {
      setLoading(false);
    }
  };

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  // Debounce search input (800ms delay - gives time to finish typing)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0); // Reset to first page on search
    }, 800);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load data when page, rowsPerPage, or search changes
  useEffect(() => {
    loadICPAnalysisData();
  }, [page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    loadMethodValidations();
    loadInstrumentStatus();
  }, []);

  const loadMethodValidations = async () => {
    // Generate method validation data for all ICP elements
    const mockValidations: MethodValidation[] = icpElements.map(element => {
      // Comprehensive detection limits for all ICP elements
      const defaultLimits = {
        // Major elements
        'Al': { lod: 0.010, loq: 0.030 },
        'Ca': { lod: 0.010, loq: 0.030 },
        'Fe': { lod: 0.010, loq: 0.030 },
        'K': { lod: 0.010, loq: 0.030 },
        'Mg': { lod: 0.001, loq: 0.003 },
        'Mn': { lod: 0.001, loq: 0.003 },
        'Na': { lod: 0.010, loq: 0.030 },
        'P': { lod: 0.010, loq: 0.030 },
        'S': { lod: 0.010, loq: 0.030 },
        'Si': { lod: 0.010, loq: 0.030 },
        'Ti': { lod: 0.005, loq: 0.015 },
        
        // Trace elements
        'Ag': { lod: 0.001, loq: 0.003 },
        'As': { lod: 0.005, loq: 0.015 },
        'Ba': { lod: 0.001, loq: 0.003 },
        'Be': { lod: 0.001, loq: 0.003 },
        'Bi': { lod: 0.005, loq: 0.015 },
        'Cd': { lod: 0.002, loq: 0.006 },
        'Co': { lod: 0.002, loq: 0.006 },
        'Cr': { lod: 0.005, loq: 0.015 },
        'Cu': { lod: 0.003, loq: 0.009 },
        'Ga': { lod: 0.005, loq: 0.015 },
        'Ge': { lod: 0.005, loq: 0.015 },
        'In': { lod: 0.005, loq: 0.015 },
        'Li': { lod: 0.005, loq: 0.015 },
        'Mo': { lod: 0.005, loq: 0.015 },
        'Nb': { lod: 0.005, loq: 0.015 },
        'Ni': { lod: 0.003, loq: 0.009 },
        'Pb': { lod: 0.005, loq: 0.015 },
        'Rb': { lod: 0.005, loq: 0.015 },
        'Sb': { lod: 0.005, loq: 0.015 },
        'Sc': { lod: 0.005, loq: 0.015 },
        'Sn': { lod: 0.005, loq: 0.015 },
        'Sr': { lod: 0.001, loq: 0.003 },
        'Ta': { lod: 0.005, loq: 0.015 },
        'Te': { lod: 0.005, loq: 0.015 },
        'Th': { lod: 0.005, loq: 0.015 },
        'U': { lod: 0.005, loq: 0.015 },
        'V': { lod: 0.005, loq: 0.015 },
        'W': { lod: 0.005, loq: 0.015 },
        'Y': { lod: 0.005, loq: 0.015 },
        'Zn': { lod: 0.002, loq: 0.006 },
        'Zr': { lod: 0.005, loq: 0.015 },
      };
      
      const limits = defaultLimits[element.symbol as keyof typeof defaultLimits] || { lod: 0.005, loq: 0.015 };
      
      return {
        element: element.symbol,
        wavelength: element.wavelength, // 🔧 FIX: Add missing wavelength property
        lod_ppm: limits.lod,
        loq_ppm: limits.loq,
        linear_range_min_ppm: limits.loq,
        linear_range_max_ppm: limits.loq * 1000,
        accuracy_percent: 95 + Math.random() * 10,
        precision_rsd_percent: 2 + Math.random() * 3,
        expanded_uncertainty_percent: 5 + Math.random() * 5,
        validation_date: new Date().toISOString(),
      };
    });
    
    setMethodValidations(mockValidations);
  };

  const loadInstrumentStatus = async () => {
    const mockStatus: InstrumentStatus = {
      last_calibration: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      r_squared: 0.9995 + Math.random() * 0.0004,
      plasma_stable: true,
      torch_hours: 1250 + Math.floor(Math.random() * 100),
      nebulizer_flow_stable: true,
      status: 'READY',
      last_qc_check: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      temperature: 20 + Math.random() * 5,
      humidity: 45 + Math.random() * 10,
    };
    
    setInstrumentStatus(mockStatus);
  };

  const handleStartAnalysis = (sample: Sample) => {
    setSelectedSample(sample);
    
    const existingAnalysis = sample.metadonnees?.icp_analysis_data;
    const preparationData = sample.metadonnees?.icp_preparation_data;
    
    if (existingAnalysis) {
      setAnalysisData(existingAnalysis);
    } else {
      setAnalysisData({
        sample_id: sample.sample_id,
        preparation_data: preparationData,
        calibration_standards: [],
        quality_controls: [],
        analysis_readings: [],
        instrument_status: instrumentStatus,
        analysis_started_at: null,
        analysis_completed_at: null,
        analyst_id: user?.id || '',
        notes: '',
        status: 'DRAFT',
      });
    }
    
    setActiveTab(0);
    setAnalysisDialog(true);
  };

  const handleCompleteAnalysis = async () => {
    if (!selectedSample) return;
    
    try {
      const completeAnalysisData = {
        ...analysisData,
        analysis_completed_at: new Date().toISOString(),
        analyst_id: user?.id,
        analyst_name: `${user?.firstName} ${user?.lastName}`,
        status: 'COMPLETED',
      };
      
      await samplesService.updateSample(selectedSample.id, {
        metadonnees: {
          ...selectedSample.metadonnees,
          selected_elements: selectedSample.metadonnees?.selected_elements, // 🔧 CRITICAL: Preserve selected_elements
          icp_analysis_data: completeAnalysisData,
        }
      });
      
      await samplesService.updateSampleStatus(
        selectedSample.id,
        SampleStatus.VALIDATION_SERVICE,
        `Analyse ICP terminée - ${analysisData.analysis_readings.length} éléments analysés | RSD moyen: ${(analysisData.analysis_readings.reduce((sum, r) => sum + (r.uncertainty || 0), 0) / analysisData.analysis_readings.length).toFixed(2)}%`,
        'LABORATOIRE_ICP'
      );
      

      
      setAnalysisDialog(false);
      setSelectedSample(null);
      loadICPAnalysisData();
      
      console.log(`✅ ICP analysis completed for sample ${selectedSample.sample_id} - moved to validation with ICP laboratory assignment`);
    } catch (error) {
      console.error('Error completing analysis:', error);
      setError('Erreur lors de la finalisation de l\'analyse');
    }
  };

  const getPriorityColor = (priority: Priority) => {
    switch (priority) {
      case Priority.URGENTE:
        return 'error';
      case Priority.ELEVEE:
        return 'warning';
      default:
        return 'success';
    }
  };

  const getInstrumentStatusColor = (status: string) => {
    switch (status) {
      case 'READY':
        return 'success';
      case 'CALIBRATING':
        return 'warning';
      case 'MAINTENANCE':
        return 'info';
      case 'ERROR':
        return 'error';
      default:
        return 'default';
    }
  };

  // Apply common settings to all batch samples - Settings stored for metadata
  const applyCommonSettingsToBatch = () => {
    console.log('⚡ Applied common ICP settings to batch:', batchCommonValues);
  };

  // ✅ FIXED: Synchronous batch analysis start (matches AU pattern - no race conditions)
  const handleStartBatchAnalysis = async (batchNumber: string, samples: Sample[]) => {
    // Open modal and set batch
    setSelectedBatch({ batchNumber, samples });
    
    // ✅ Reset import state for new batch
    setImportedFileName(null);
    
    // 🚀 PERFORMANCE: Reset virtualization state for new batch
    setMaxVisibleSamples(6);
    setShowAllSamples(false);
    setIsLoadingMoreSamples(false);
    
    // ✅ FIXED: Initialize synchronously (no await - matches AU pattern)
    initializeBatchAnalysisData(samples);
    
    // Open dialog after initialization
    setBatchAnalysisDialog(true);
  };

  // 🚀 PERFORMANCE: Progressive sample loading for large batches
  const handleLoadMoreSamples = useCallback(async () => {
    if (!selectedBatch || isLoadingMoreSamples) return;
    
    setIsLoadingMoreSamples(true);
    console.log(`🚀 Loading more samples: ${maxVisibleSamples} → ALL ${selectedBatch.samples.length}`);
    
    // Small delay for smooth UX
    await new Promise(resolve => setTimeout(resolve, 200));
    
    setShowAllSamples(true);
    setMaxVisibleSamples(selectedBatch.samples.length);
    setIsLoadingMoreSamples(false);
    
    console.log(`✅ All ${selectedBatch.samples.length} samples loaded successfully`);
  }, [selectedBatch, maxVisibleSamples, isLoadingMoreSamples]);

  // 🚀 PERFORMANCE: Get samples to render (virtual slice)
  const getSamplesToRender = useCallback(() => {
    if (!selectedBatch) return [];
    
    if (showAllSamples) {
      return selectedBatch.samples;
    }
    
    return selectedBatch.samples.slice(0, maxVisibleSamples);
  }, [selectedBatch, maxVisibleSamples, showAllSamples]);

  // 🚀 PROFESSIONAL LIMS: Virtual scrolling calculations
  const getVirtualizedSamples = useCallback(() => {
    if (!selectedBatch || !virtualScrollEnabled) {
      return selectedBatch?.samples || [];
    }
    
    // Calculate which rows should be visible based on scroll position
    const startIndex = visibleRowStart;
    const endIndex = Math.min(startIndex + visibleRowCount, selectedBatch.samples.length);
    
    return selectedBatch.samples.slice(startIndex, endIndex);
  }, [selectedBatch, virtualScrollEnabled, visibleRowStart, visibleRowCount]);

  // 🚀 PROFESSIONAL LIMS: Virtual scroll handler
  const handleVirtualScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (!selectedBatch || !virtualScrollEnabled) return;
    
    const scrollTop = e.currentTarget.scrollTop;
    const newStartIndex = Math.floor(scrollTop / rowHeight);
    const maxStartIndex = Math.max(0, selectedBatch.samples.length - visibleRowCount);
    
    setVisibleRowStart(Math.min(newStartIndex, maxStartIndex));
  }, [selectedBatch, virtualScrollEnabled, rowHeight, visibleRowCount]);

  // 🚀 PROFESSIONAL LIMS: Native input component with local state for smooth editing
  const NativeNumberInput = memo(({ 
    value, 
    onChange, 
    sampleId, 
    element,
    hasValue 
  }: {
    value: number | null;
    onChange: (sampleId: string, element: string, value: number) => void;
    sampleId: string;
    element: string;
    hasValue: boolean;
  }) => {
    // 🎯 PERFORMANCE FIX: Local state buffer for smooth typing (decimals, dots, commas)
    const [localValue, setLocalValue] = useState<string>('');
    const [isFocused, setIsFocused] = useState(false);
    
    // Sync with parent value when not focused
    useEffect(() => {
      if (!isFocused) {
        setLocalValue(value === null ? '' : String(value));
      }
    }, [value, isFocused]);
    
    const handleBlur = () => {
      setIsFocused(false);
      const numValue = parseFloat(localValue);
      
      // ✅ SAFETY: Only accept valid positive numbers
      if (!isNaN(numValue) && numValue >= 0) {
        onChange(sampleId, element, numValue);
      } else if (localValue === '') {
        // Empty field -> set to 0
        onChange(sampleId, element, 0);
      } else {
        // ✅ SAFETY: Invalid input -> revert to previous valid value
        setLocalValue(value === null ? '' : String(value));
      }
    };
    
    return (
      <input
        type="text"
        inputMode="decimal"
        value={isFocused ? localValue : (value === null ? '' : value)}
        onChange={(e) => {
          setLocalValue(e.target.value);
        }}
        onBlur={handleBlur}
        onFocus={(e) => {
          setIsFocused(true);
          setLocalValue(e.target.value);
          e.target.select();
        }}
        style={{
          width: '100%',
          height: '28px',
          border: hasValue ? '2px solid #4caf50' : '1px solid #ddd',
          borderRadius: '4px',
          padding: '2px 4px',
          fontSize: '0.75rem',
          textAlign: 'center',
          backgroundColor: hasValue ? '#e8f5e8' : 'transparent',
          outline: 'none',
          boxSizing: 'border-box'
        }}
      />
    );
  });

  // 🚀 PERFORMANCE: Optimized change handler for native inputs
  // For major elements/oxides the user enters the value in %; we mirror it into
  // ppm (×10 000) so that downstream certificate generation, LOD/LOQ checks and
  // batch-analysis completion (which only read .ppm) continue to work correctly.
  const handleNativeInputChange = useCallback((sampleId: string, element: string, value: number) => {
    const asPercent = isPercentDisplayElement(element);
    updateBatchAnalysisData(sampleId, element, {
      ppm: asPercent ? value * 10000 : value,
      percent: asPercent ? value : value / 10000,
      timestamp: new Date().toISOString()
    });
  }, [updateBatchAnalysisData]);

  // ✅ SAFETY: Force blur on any focused input before closing dialog
  const handleCloseBatchDialog = useCallback(() => {
    // Force commit any focused input value
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    // Small delay to ensure blur completes
    setTimeout(() => {
      setBatchAnalysisDialog(false);
    }, 10);
  }, []);

  // Save batch analysis and move all samples to validation
  const handleCompleteBatchAnalysis = async () => {
    if (!selectedBatch) return;

    try {
      setLoading(true);
      
      // 🔧 DATA PREPARATION: Prepare all sample data before sequential processing
      console.log(`🔄 Processing ${selectedBatch.samples.length} samples SEQUENTIALLY for batch analysis...`);
      
      const elementsToAnalyze = getBatchElementOrder();
      
      // Prepare all sample updates
      const sampleUpdates = selectedBatch.samples.map(sample => {
        const sampleAnalysisData = batchAnalysisData[sample.id] || {};
        const prepData = sample.metadonnees?.icp_preparation;
        
        console.log(`🔍 ICP Sample ${sample.sample_id}:`, {
          selected_elements: getBatchElementOrder(),
          elementsToAnalyze,
          sampleAnalysisData: Object.keys(sampleAnalysisData),
          sampleAnalysisDataFull: sampleAnalysisData
        });
        
        // Construct analysis readings from batch data
        const analysisReadings: AnalysisReading[] = [];
        
        elementsToAnalyze.forEach((element: string) => {
          const elementData = sampleAnalysisData[element];
          console.log(`🔍 Element ${element} for sample ${sample.sample_id}:`, {
            elementData,
            hasElementData: !!elementData,
            ppm: elementData?.ppm,
            ppmIsValid: elementData?.ppm >= 0
          });
          
          if (elementData && elementData.ppm >= 0) {
            const ppm = elementData.ppm;
            // Use pre-computed percent from backend if available (handles fusion % vs 4-acides ppm correctly)
            const percent = elementData.percent != null ? elementData.percent : ppm / 10000;

            // Check detection limits
            const validation = methodValidations.find(v => v.element === element);
            const elementInfo = icpElements.find(e => e.symbol === element);

            const reading: AnalysisReading = {
              id: `${sample.id}-${element}-${Date.now()}`,
              element: element,
              intensity: elementData.ppm || 0, // Store original lecture/reading value
              absorbance: elementData.ppm || 0, // Also store in absorbance for consistency with SAA
              concentration_ppm: ppm,
              concentration_final_percent: percent,
              uncertainty: validation?.expanded_uncertainty_percent || 5.0,
              detection_limit: validation?.lod_ppm || 0.005,
              timestamp: elementData.timestamp || new Date().toISOString(),
              instrument_settings: {
                wavelength: elementInfo?.wavelength || 0,
                rf_power: batchCommonValues.plasmaPower,
                nebulizer_flow: 1.0,
                integration_time: 10
              }
            };
            
            analysisReadings.push(reading);
          }
        });
        
        // Construct complete analysis form with dilution factor
        const completedForm: ICPAnalysisForm = {
          sample_id: sample.sample_id,
          preparation_data: prepData ? {
            initial_weight: prepData.initialWeight || 0.5,
            digestion_method: prepData.digestionMethod || 'HNO3/HCl',
            initial_volume: prepData.initialVolume || 25,
            dilutions: prepData.dilutions || [],
            total_dilution_factor: prepData.totalDilutionFactor || 1,
            dilution_factor: prepData.totalDilutionFactor || 1, // Add alternative name for compatibility
            preparation_completed_at: prepData.timestamp || new Date().toISOString(),
            technician_id: prepData.technician_id || 'unknown',
            technician_name: prepData.technician_name || 'Technicien ICP',
          } : null,
          calibration_standards: [{
            id: 'batch-icp-standard',
            element: 'Multi-element',
            concentration_ppm: 1000,
            intensity: 0,
            used_at: new Date().toISOString()
          }],
          quality_controls: [],
          analysis_readings: analysisReadings,
          instrument_status: instrumentStatus,
          analysis_started_at: new Date().toISOString(),
          analysis_completed_at: new Date().toISOString(),
          analyst_id: user?.id || '',
          notes: `Analyse ICP en lot - Direct ppm input - Batch: ${selectedBatch.batchNumber}, Standard: ${batchCommonValues.calibrationStandard}`,
          status: 'COMPLETED',
          analysis_type: currentBatchAnalysisType || 'unknown',
          digestion_method: currentBatchAnalysisType === 'fusion'
            ? 'Fusion Na2O2 - PRE.MO/ANA/007'
            : '4 Acides - PRE.MO/ANA/037',
        };
        
        return {
          sample,
          metadonnees: {
            ...sample.metadonnees,
            selected_elements: sample.metadonnees?.selected_elements || elementsToAnalyze, // 🔧 CRITICAL: Preserve selected_elements
            icp_analysis_form: completedForm,
            // 🔧 CRITICAL FIX: MERGE with existing AU/SAA data instead of overwriting
            analysis_results: [
              // Preserve existing AU/SAA data (filter out ICP elements to avoid duplicates)
              ...(sample.metadonnees?.analysis_results || []).filter((r: any) => 
                !elementsToAnalyze.includes(r.element)
              ),
              // Add new ICP data
              ...analysisReadings.map(reading => ({
              element: reading.element,
              concentration_ppm: reading.concentration_ppm,
              concentration_final_percent: reading.concentration_final_percent,
              intensity: 0,
              detection_status: (() => {
                const validation = methodValidations.find(v => v.element === reading.element);
                if (!validation) return 'VALID';
                if (reading.concentration_ppm < validation.lod_ppm) return 'BELOW_LOD';
                if (reading.concentration_ppm < validation.loq_ppm) return 'BELOW_LOQ';
                return 'VALID';
              })(),
              uncertainty: reading.uncertainty,
              timestamp: reading.timestamp
              }))
            ],
            icp_analysis_completed_at: new Date().toISOString(),
            icp_analyst_id: user?.id,
            icp_analyst_name: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
            analysis_status: 'COMPLETED',
            batch_analysis: true,
            batch_analysis_summary: {
              batch_number: selectedBatch.batchNumber,
              is_batch_sample: true,
              elements_analyzed: elementsToAnalyze,
              total_readings: analysisReadings.length,
              direct_ppm_input: true,
              calibration_standard: batchCommonValues.calibrationStandard,
              detection_method: batchCommonValues.detectionMethod,
              completion_date: new Date().toISOString(),
              below_lod_count: analysisReadings.filter(r => {
                const validation = methodValidations.find(v => v.element === r.element);
                return validation ? r.concentration_ppm < validation.lod_ppm : false;
              }).length,
              imported_file: importedFileName || undefined
            }
          },
          analysisReadings
        };
      });

      // 🚀 BATCH OPTIMIZATION: Build sample-specific updates for batch API
      console.log(`🔄 Batch updating ${sampleUpdates.length} ICP analysis samples...`);
      
      const sampleIds = sampleUpdates.map(({ sample }) => sample.id);
      const batchUpdates = {};
      
      // Common status for all samples (they all go to validation)
      const newStatus = SampleStatus.VALIDATION_SERVICE;
      
      // Build sample-specific metadata updates
      sampleUpdates.forEach(({ sample, metadonnees, analysisReadings }) => {
        const isMultiLabSample = sample.metadonnees?.multi_lab_workflow === true || 
                                 sample.metadonnees?.bgm_method_selected === true;
        
        console.log(`📊 Sample ${sample.sample_id} analysis data:`, {
          elementsAnalyzed: analysisReadings.length,
          isMultiLabSample,
          analysisReadings: analysisReadings.map(r => ({ element: r.element, ppm: r.concentration_ppm }))
        });
        
        // Build sample-specific metadata (preserves existing metadata automatically)
        let sampleMetadata = metadonnees;
        
        // For multi-lab samples (BGM or custom), add ICP completion flag
        if (isMultiLabSample) {
          sampleMetadata = {
            ...metadonnees,
            icp_analysis_completed: true
          };
        }
        
        batchUpdates[sample.id] = sampleMetadata;
      });
      
      // Generate status comment for batch
      const isMultiLabBatch = sampleUpdates.some(({ sample }) => 
        sample.metadonnees?.multi_lab_workflow === true || sample.metadonnees?.bgm_method_selected === true
      );
      const statusComment = isMultiLabBatch 
        ? `Analyse ICP LOT terminée (multi-lab) - Batch: ${selectedBatch.batchNumber}`
        : `Analyse ICP LOT terminée - Direct ppm input - Batch: ${selectedBatch.batchNumber}, Éléments: ${elementsToAnalyze.join(', ')}`;

      // 🚀 SINGLE BATCH API CALL: Update all samples with sample-specific metadata + status
      console.log(`📦 Batch updates object:`, batchUpdates);
      console.log(`📋 Status update:`, { status: newStatus, comment: statusComment });
      
      const result = await samplesService.updateSampleBatch(
        sampleIds,
        batchUpdates,  // Sample-specific metadata updates with analysis data
        { status: newStatus, comment: statusComment }
      );
      
      console.log(`✅ Batch update completed: ${result.updated} samples updated, ${result.errors.length} errors`);
      if (result.errors.length > 0) {
        console.warn('⚠️ Some samples had errors:', result.errors);
        alert(`⚠️ Analyse terminée avec quelques erreurs:\n${result.errors.join('\n')}`);
      }
      
      console.log(`✅ SUCCESS: ${selectedBatch.samples.length} samples processed and moved to validation`);

      console.log(`✅ ICP Batch ${selectedBatch.batchNumber} analysis completed - ${selectedBatch.samples.length} samples moved to validation`);
      
      // ✅ Archive imported file if data came from import
      if (importedFileName) {
        try {
          const { archivedFile } = await apiService.post<{ archivedFile: string }>('/icp/import-file', {
            filename: importedFileName,
            batchSamples: selectedBatch.samples.map(s => ({
              id: s.id,
              sample_id: s.sample_id
            })),
            archiveOnly: true // New flag to only archive, don't save data again
          });
          console.log(`✅ File archived: ${archivedFile}`);
        } catch (archiveError) {
          console.error('Warning: Could not archive imported file:', archiveError);
          // Don't fail the whole operation if archiving fails
        }
      }
      
      // Force blur before closing to ensure any unsaved edits are committed
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      
      // Close dialog with slight delay to ensure blur completes
      setTimeout(() => {
        setBatchAnalysisDialog(false);
        setSelectedBatch(null);
        setBatchAnalysisData({});
        setImportedFileName(null); // Reset imported file tracking
        setBatchCommonValues({
          calibrationStandard: 'Multi-element Standard 1000 ppm',
          plasmaPower: 1150,
          nebulizer: 'Concentric Glass',
          detectionMethod: 'Inductively Coupled Plasma Optical Emission Spectrometry'
        });
        loadICPAnalysisData(); // Refresh the dashboard
        
        const importMessage = importedFileName ? ` (données importées depuis ${importedFileName})` : '';
        setSnackbarMessage(`✅ Analyse ICP du lot ${selectedBatch.batchNumber} complétée${importMessage} - ${selectedBatch.samples.length} échantillons envoyés pour validation`);
        setSnackbarOpen(true);
      }, 10);
      
    } catch (error: any) {
      console.error('❌ Error completing ICP batch analysis:', error);
      setError(`Erreur lors de la finalisation du lot ICP: ${error?.message || 'Erreur inconnue'}`);
    } finally {
      setLoading(false);
    }
  };

  // 🔥 RESTORED: Send batch back to chemical preparation (SEQUENTIAL - WORKS!)
  const handleSendBatchToPrepChimique = useCallback(async (batchNumber: string, batchSamples: Sample[]) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir renvoyer le lot ${batchNumber} vers la préparation chimique ICP ?`)) {
      return;
    }
    
    try {
      setSendingToPrepBatch(true);
      
      console.log(`🔄 Sending batch ${batchNumber} back to prep chimique...`);
      
      // 🔧 FIXED: Back to SEQUENTIAL processing - prevents race conditions
      for (const sample of batchSamples) {
        await samplesService.updateSampleStatus(
              sample.id, 
              SampleStatus.LABORATOIRE_ICP_PREP_CHIMIQUE, 
              `Renvoyé vers préparation chimique ICP - Lot: ${batchNumber}`
        );
        
        // 🔧 CRITICAL FIX: Reset preparation flags for re-preparation (handles both BGM and non-BGM)
        await samplesService.updateSample(sample.id, {
              metadonnees: {
                ...sample.metadonnees,
                icp_analysis_data: null,  // Clear analysis data
                icp_preparation_completed: false,  // Reset prep completion flag
                requires_repreparation: true,  // Flag for re-preparation
                repreparation_reason: 'Renvoyé depuis Analyste ICP',
                repreparation_requested_at: new Date().toISOString(),
              }
        });
      }
      
      // Reload the data to reflect changes
      await loadICPAnalysisData();
      
      console.log(`✅ Successfully sent batch ${batchNumber} back to prep chimique`);
      
      setSnackbarMessage(`Lot ${batchNumber} renvoyé vers la préparation chimique ICP avec succès`);
      setSnackbarOpen(true);
      
    } catch (error) {
      console.error('❌ Erreur lors du renvoi vers la préparation:', error);
      setSnackbarMessage(`Erreur lors du renvoi: ${error.message || 'Erreur inconnue'}`);
      setSnackbarOpen(true);
    } finally {
      setSendingToPrepBatch(false);
    }
  }, []);

  const toggleBatchExpansion = (batchNumber: string) => {
    const newExpandedBatches = new Set(expandedBatches);
    if (newExpandedBatches.has(batchNumber)) {
      newExpandedBatches.delete(batchNumber);
    } else {
      newExpandedBatches.add(batchNumber);
    }
    setExpandedBatches(newExpandedBatches);
  };

  // NEW: Directory Scanning and Import Functions
  const scanICPDirectory = async () => {
    try {
      setImportLoading(true);
      const { files } = await apiService.get<{ files: any[] }>('/icp/scan-directory');
      
      // Match files with current batch samples
      const batchSamples = selectedBatch?.samples.map(s => ({
        id: s.id,
        sample_id: s.sample_id
      })) || [];
      
      const filesWithMatches = await Promise.all(
        files.map(async (file: any) => {
          const matches = await checkFileMatches(file.path, batchSamples);
          return {
            filename: file.name,
            path: file.path,
            size: file.size,
            modified: file.modified,
            matchedSamples: matches,
            analysisType: file.analysisType || 'unknown'
          } as ICPFileInfo;
        })
      );
      
      setAvailableFiles(filesWithMatches);
      setImportDialogOpen(true);
    } catch (error) {
      console.error('Error scanning directory:', error);
      setError('Erreur lors du scan du répertoire ICP');
    } finally {
      setImportLoading(false);
    }
  };
  
  const checkFileMatches = async (filePath: string, batchSamples: Array<{id: string, sample_id: string}>): Promise<string[]> => {
    try {
      const { matches } = await apiService.post<{ matches: string[] }>('/icp/check-file-matches', {
        filePath,
        batchSamples
      });
      return matches;
    } catch (error) {
      console.error('Error checking file matches:', error);
      return [];
    }
  };

  const applyImportResults = (results: ICPImportResult[], analysisType: ICPAnalysisType, fileLabel: string) => {
    if (!selectedBatch) return;
    
    const expectedElements = getBatchElementOrder();
    
    const newBatchData: Record<string, Record<string, { ppm: number; percent?: number; timestamp: string }>> = {};
    Object.keys(batchAnalysisData).forEach(sampleId => {
      newBatchData[sampleId] = {};
      Object.keys(batchAnalysisData[sampleId]).forEach(element => {
        newBatchData[sampleId][element] = { ...batchAnalysisData[sampleId][element] };
      });
    });
  
    selectedBatch.samples.forEach(sample => {
      if (!newBatchData[sample.id]) {
        newBatchData[sample.id] = {};
        expectedElements.forEach(element => {
          newBatchData[sample.id][element] = { ppm: 0, percent: 0, timestamp: new Date().toISOString() };
        });
      }
    });
    
    // The backend parser converts oxide names to raw symbols (Al2O3→Al) via oxideToElementMap.
    // But expectedElements may now use oxide names (Al2O3, CaO…) after the DB migration.
    // This map lets us match a raw symbol back to its oxide form in expectedElements.
    const rawToOxide: Record<string, string> = {
      Al: 'Al2O3', Ca: 'CaO', Fe: 'Fe2O3', K: 'K2O',
      Mg: 'MgO', Mn: 'MnO', Na: 'Na2O', P: 'P2O5', Si: 'SiO2', Ti: 'TiO2',
    };

    results.forEach((result: ICPImportResult) => {
      // Trim both sides: Agilent fusion CSVs sometimes pad labels with a trailing space
      const refClean = (result.sample_reference || '').trim();
      const sample = selectedBatch.samples.find(s => (s.sample_id || '').trim() === refClean);
      if (!sample) return;

      // Resolve the key to use in batchAnalysisData:
      // 1. If result.element matches an expected element directly → use as-is
      // 2. If result.element is a raw symbol whose oxide form is expected → store under oxide key
      // 3. Otherwise skip (element not requested for this batch)
      let storeKey: string | null = null;
      if (expectedElements.includes(result.element)) {
        storeKey = result.element;
      } else {
        const oxideAlias = rawToOxide[result.element];
        if (oxideAlias && expectedElements.includes(oxideAlias)) {
          storeKey = oxideAlias;
        }
      }

      if (storeKey) {
        newBatchData[sample.id][storeKey] = {
          ppm: result.concentration_ppm,
          percent: result.concentration_percent,
          timestamp: result.timestamp,
        };
      }
    });
    
    setBatchAnalysisData(newBatchData);
    setCurrentBatchAnalysisType(analysisType);
    setSnackbarMessage(`Import réussi! ${results.length} résultats ICP importés (${fileLabel})`);
    setSnackbarOpen(true);
  };

  const handleImportICPFile = async (filename: string) => {
    try {
      setImportLoading(true);
      
      if (selectedBatch) {
        const batchSampleData = selectedBatch.samples.map(sample => ({ 
          id: sample.id, 
          sample_id: sample.sample_id 
        }));
        
        const response = await apiService.post<{ 
          results: ICPImportResult[]; 
          totalSamples: number; 
          totalElements: number;
          analysisType: ICPAnalysisType;
          importSummary?: {
            methodElements: string[];
            importedElements: string[];
            missingElements: string[];
            filteredOutElements: string[];
          };
        }>('/icp/parse-file', {
          filename,
          batchSamples: batchSampleData
        });

        applyImportResults(response.results, response.analysisType, filename);
        setImportDialogOpen(false);
      }
      
    } catch (error) {
      console.error('Error importing ICP file:', error);
      setError('Erreur lors de l\'import du fichier ICP');
    } finally {
      setImportLoading(false);
    }
  };

  const handleMultiFileImport = async () => {
    if (selectedFilesForImport.size === 0 || !selectedBatch) return;
    
    try {
      setImportLoading(true);
      const filenames = Array.from(selectedFilesForImport);
      const batchSampleData = selectedBatch.samples.map(sample => ({ 
        id: sample.id, 
        sample_id: sample.sample_id 
      }));

      if (filenames.length === 1) {
        await handleImportICPFile(filenames[0]);
        return;
      }

      const response = await apiService.post<{ 
        results: ICPImportResult[];
        totalSamples: number;
        totalElements: number;
        analysisType: ICPAnalysisType;
        files: Array<{ filename: string; analysisType: ICPAnalysisType; count: number }>;
        mergeStrategy: string;
        importSummary?: any;
      }>('/icp/parse-multi-files', {
        filenames,
        batchSamples: batchSampleData,
        mergeStrategy: 'fusion-priority'
      });

      const fileLabel = response.files?.map(f => `${f.filename} (${f.analysisType})`).join(' + ') || filenames.join(' + ');
      applyImportResults(response.results, response.analysisType, fileLabel);
      setImportDialogOpen(false);
      setSelectedFilesForImport(new Set());
    } catch (error) {
      console.error('Error importing multiple ICP files:', error);
      setError('Erreur lors de l\'import des fichiers ICP');
    } finally {
      setImportLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
  return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  // 🔧 CRITICAL FIX: Properly destructure batches and convert to array format
  const { batches: batchesObject, individualSamples: batchedIndividualSamples } = groupSamplesByBatch(samples.filter(s => s.metadonnees?.batch_info?.batch_number));
  const individualSamples = samples.filter(s => !s.metadonnees?.batch_info?.batch_number);
  
  // Convert batches object to array format for rendering (like the original)
  const batches = Object.entries(batchesObject).map(([batchNumber, batchSamples]) => ({
    batchNumber,
    samples: batchSamples.sort((a, b) => (a.metadonnees?.batch_info?.sample_position || 0) - (b.metadonnees?.batch_info?.sample_position || 0)),
    totalSamples: batchSamples[0]?.metadonnees?.batch_info?.total_samples || batchSamples.length,
    isComplete: batchSamples.length === (batchSamples[0]?.metadonnees?.batch_info?.total_samples || batchSamples.length),
    creationDate: batchSamples[0]?.date_creation // For sorting
  })).sort((a, b) => {
    // Sort batches by oldest first (FIFO workflow)
    const dateA = new Date(a.creationDate || 0).getTime();
    const dateB = new Date(b.creationDate || 0).getTime();
    return dateA - dateB;
  });

  return (
    <Box sx={{ 
      p: 3, 
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.8)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
    }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <ScienceIcon color="primary" sx={{ fontSize: 40 }} />
        Tableau de Bord - Analyste ICP
        <Chip 
          label={`${samples.length} échantillons`}
          color="primary" 
          variant="outlined"
        />
      </Typography>

      {/* Enhanced Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.primary.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.primary.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                    Prêts pour analyse
                  </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.ready_for_analysis}
                  </Typography>
                </Box>
    <Box sx={{ 
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  ⏳
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
      <Card sx={{ 
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.warning.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.warning.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.warning.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                    En cours
                  </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.in_progress}
              </Typography>
                </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.warning.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  🔄
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.success.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.success.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.success.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                    Terminés aujourd'hui
                  </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.completed_today}
              </Typography>
            </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.success.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  ✅
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.error.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.error.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.error.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                    Urgents
              </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.urgent_samples}
              </Typography>
            </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.error.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  🚨
            </Box>
          </Box>
        </CardContent>
      </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.primary.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.primary.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                    Éléments analysés
                  </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.total_elements_analyzed}
                  </Typography>
                </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  🔬
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.info.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.info.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.info.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                    RSD Moyen
                    </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.average_rsd.toFixed(1)}%
                    </Typography>
                  </Box>
                  <Box sx={{
                    width: 60,
                    height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.info.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                  }}>
                  🎯
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
      </Grid>

      {/* Instrument Status Card */}
      <Card sx={{ 
        mb: 3,
        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 30px ${alpha(theme.palette.success.main, 0.2)}`,
        }
      }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            background: `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold'
          }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem',
              boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.3)}`,
            }}>
              ⚡
            </Box>
            État de l'Instrument ICP
            <Chip 
              label={instrumentStatus.status}
              color={getInstrumentStatusColor(instrumentStatus.status) as any}
              size="small"
              sx={{
                background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                color: 'white',
                fontWeight: 'bold',
                boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.3)}`,
              }}
            />
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{
                p: 2,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                transition: 'transform 0.2s ease',
                '&:hover': { transform: 'translateX(4px)' }
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                  📊 R² Calibration
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                  {instrumentStatus.r_squared.toFixed(4)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{
                p: 2,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)}, ${alpha(theme.palette.warning.main, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                transition: 'transform 0.2s ease',
                '&:hover': { transform: 'translateX(4px)' }
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                  🔥 Heures Torche
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                  {instrumentStatus.torch_hours}h
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{
                p: 2,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)}, ${alpha(theme.palette.info.main, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                transition: 'transform 0.2s ease',
                '&:hover': { transform: 'translateX(4px)' }
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                  🌡️ Température
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                  {instrumentStatus.temperature.toFixed(1)}°C
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{
                p: 2,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                transition: 'transform 0.2s ease',
                '&:hover': { transform: 'translateX(4px)' }
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                  💧 Humidité
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                  {instrumentStatus.humidity.toFixed(1)}%
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Batch Analysis Section */}
      {batches.length > 0 && (
        <Card sx={{ 
          mb: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
          border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 8px 30px ${alpha(theme.palette.info.main, 0.2)}`,
          }
        }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              background: `linear-gradient(45deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold'
            }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.2rem',
                boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.3)}`,
              }}>
                📋
              </Box>
              Analyse par Lots
              {totalSamples > 0 && (
                <Chip 
                  label={`${totalSamples} total • ${batchCount} lots`} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ ml: 2 }}
                />
              )}
              </Typography>
              
              {/* 🔍 SEARCH BAR */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Rechercher par ID ou N° Lot..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ 
                    width: { xs: '100%', sm: 300 },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleClearSearch}>
                          <Clear fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            
            {batches.map((batchItem, index) => {
              // 🔧 CRITICAL FIX: Detect rejected batches for visual differentiation
              const isRejectedBatch = batchItem.samples.some(s => 
                s.metadonnees?.validation_data?.action === 'reject' ||
                s.metadonnees?.requires_reanalysis === true
              );
              
              return (
              <Accordion 
                key={batchItem.batchNumber} 
                expanded={expandedBatches.has(batchItem.batchNumber)} 
                onChange={() => toggleBatchExpansion(batchItem.batchNumber)}
                sx={{ 
                  mb: 1,
                  background: isRejectedBatch
                    ? `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.dark, 0.08)})`
                    : index % 2 === 0 
                      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.info.main, 0.02)})`
                      : `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.02)}, ${alpha(theme.palette.warning.main, 0.02)})`,
                  border: isRejectedBatch 
                    ? `2px solid ${theme.palette.error.main}`
                    : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  borderRadius: '12px !important',
                  overflow: 'hidden',
                  '&:before': { display: 'none' },
                  '&:hover': {
                    transform: 'translateX(4px)',
                    transition: 'transform 0.2s ease',
                  }
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    background: isRejectedBatch
                      ? `linear-gradient(90deg, ${alpha(theme.palette.error.main, 0.12)}, ${alpha(theme.palette.error.dark, 0.12)})`
                      : `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.info.main, 0.05)})`,
                    borderRadius: '12px',
                    '&:hover': {
                      background: isRejectedBatch
                        ? `linear-gradient(90deg, ${alpha(theme.palette.error.main, 0.18)}, ${alpha(theme.palette.error.dark, 0.18)})`
                        : `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.info.main, 0.1)})`,
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Typography variant="subtitle1" sx={{ 
                      fontWeight: 'bold',
                      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.info.main})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}>
                      {batchItem.batchNumber}
              </Typography>
                    <Box display="flex" gap={1} mr={2}>
                      <Chip 
                        label={`${batchItem.samples.length}/${batchItem.totalSamples} échantillons`}
                        color={batchItem.isComplete ? 'success' : 'warning'}
                        size="small"
                        sx={{
                          background: batchItem.isComplete 
                            ? `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                            : `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                          color: 'white',
                          fontWeight: 'bold',
                          boxShadow: `0 2px 8px ${alpha(batchItem.isComplete ? theme.palette.success.main : theme.palette.warning.main, 0.3)}`,
                        }}
                      />
                      <Chip 
                        label={batchItem.isComplete ? 'Complet' : 'En cours'}
                        color={batchItem.isComplete ? 'success' : 'info'}
                        size="small"
                        sx={{
                          background: batchItem.isComplete 
                            ? `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                            : `linear-gradient(45deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                          color: 'white',
                          fontWeight: 'bold',
                          boxShadow: `0 2px 8px ${alpha(batchItem.isComplete ? theme.palette.success.main : theme.palette.info.main, 0.3)}`,
                        }}
                      />
                      
                      {/* 🆕 Rejection indicator chip */}
                      {isRejectedBatch && (
                        <Chip
                          label="⚠️ REJET - REANALYSE REQUISE"
                          size="small"
                          color="error"
                          variant="filled"
                          sx={{ 
                            fontWeight: 'bold',
                            '@keyframes pulse': {
                              '0%': { opacity: 1 },
                              '50%': { opacity: 0.7 },
                              '100%': { opacity: 1 }
                            },
                            animation: 'pulse 2s infinite',
                            background: `linear-gradient(45deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                            color: 'white',
                            boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.4)}`,
                          }}
                        />
                      )}
                      
                      {/* 🆕 NEW: ICP File availability indicator */}
                      {(() => {
                        const fileStatus = batchItem.samples[0]?.metadonnees?.file_status?.ICP;
                        const hasFileReady = fileStatus?.available === true;
                        const fileName = fileStatus?.fileName;
                        
                        if (hasFileReady) {
                          return (
                            <Chip 
                              label={`📁 FILE READY: ${fileName}`}
                              color="success"
                              variant="filled"
                              size="small"
                              sx={{
                                fontWeight: 'bold',
                                background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                                color: 'white',
                                '@keyframes pulse': {
                                  '0%': { opacity: 1 },
                                  '50%': { opacity: 0.7 },
                                  '100%': { opacity: 1 }
                                },
                                animation: 'pulse 2s infinite',
                                boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.4)}`,
                              }}
                            />
                          );
                        } else {
                          return (
                            <Chip 
                              label="⏳ WAITING FOR FILE"
                              color="default"
                              variant="outlined"
                              size="small"
                              sx={{
                                fontWeight: 'bold',
                                borderColor: theme.palette.grey[400],
                                color: theme.palette.grey[600],
                              }}
                            />
                          );
                        }
                      })()}
                      <Button
                        size="small"
                        variant="contained"
                        color={isRejectedBatch ? "error" : "primary"}
                        startIcon={isRejectedBatch ? <WarningIcon /> : <AnalyticsIcon />}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent batch expansion when clicking button
                          handleStartBatchAnalysis(batchItem.batchNumber, batchItem.samples);
                        }}
                        sx={{ 
                          ml: 1,
                          background: isRejectedBatch
                            ? `linear-gradient(45deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`
                            : `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                          boxShadow: isRejectedBatch
                            ? `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`
                            : `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            background: isRejectedBatch
                              ? `linear-gradient(45deg, ${theme.palette.error.dark}, ${theme.palette.error.main})`
                              : `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                            transform: 'translateY(-2px)',
                            boxShadow: isRejectedBatch
                              ? `0 8px 20px ${alpha(theme.palette.error.main, 0.4)}`
                              : `0 8px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                          }
                        }}
                      >
                        {isRejectedBatch ? 'Re-Analyser le Lot' : 'Analyser le Lot'}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={sendingToPrepBatch ? <CircularProgress size={16} /> : <Settings />}
                        disabled={sendingToPrepBatch}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSendBatchToPrepChimique(batchItem.batchNumber, batchItem.samples);
                        }}
                        sx={{ 
                          ml: 1,
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.warning.main, 0.1),
                            borderColor: theme.palette.warning.main,
                          }
                        }}
                      >
                        {sendingToPrepBatch ? 'Envoi...' : 'Vers Prep'}
                      </Button>
            </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {/* 🚀 PERFORMANCE: Only render table when accordion is expanded */}
                  {expandedBatches.has(batchItem.batchNumber) && (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Position</TableCell>
                          <TableCell>Sample ID</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Priorité</TableCell>
                          <TableCell>État</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {batchItem.samples.map((sample) => (
                          <TableRow key={sample.id}>
                            <TableCell>{sample.metadonnees?.batch_info?.sample_position || 0}</TableCell>
                            <TableCell>{sample.sample_id}</TableCell>
                            <TableCell>{sample.client?.company_name || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip 
                                label={sample.priorite}
                                color={getPriorityColor(sample.priorite)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {sample.metadonnees?.icp_analysis_data?.analysis_readings?.length > 0 ? (
                                <Chip label="Analysé" color="success" size="small" />
                              ) : (
                                <Chip label="En attente" color="default" size="small" />
                              )}
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleStartAnalysis(sample)}
                                startIcon={<AnalyticsIcon />}
                              >
                                Analyser
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </AccordionDetails>
              </Accordion>
              );
            })}
            
            {/* 📄 PAGINATION CONTROLS */}
            <TablePagination
              component="div"
              count={totalSamples}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[50, 100, 250, 500]}
              labelRowsPerPage="Échantillons par page:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} sur ${count} échantillons (${batchCount} lots sur cette page)`
              }
              sx={{
                borderTop: '1px solid #e0e0e0',
                mt: 2,
                '& .MuiTablePagination-toolbar': {
                  backgroundColor: '#f5f5f5',
                }
              }}
            />
          </CardContent>
        </Card>
      )}

      {/* Individual Samples */}
      <Card sx={{
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.2)}`,
        }
      }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold'
          }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem',
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
            }}>
              🔬
            </Box>
            Échantillons Individuels ({individualSamples.length})
          </Typography>
          
          {individualSamples.length === 0 ? (
            <Alert 
              severity="info"
              sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)}, ${alpha(theme.palette.info.main, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  color: theme.palette.info.main
                }
              }}
            >
              Aucun échantillon individuel en attente d'analyse ICP.
            </Alert>
          ) : (
            <List>
              {individualSamples.map((sample, index) => {
                const analysisReadings = sample.metadonnees?.icp_analysis_data?.analysis_readings || [];
                const preparationData = sample.metadonnees?.icp_preparation_data;
                
                return (
                  <ListItem
                    key={sample.id}
                    sx={{
                      border: '1px solid',
                      borderColor: alpha(theme.palette.divider, 0.2),
                      borderRadius: 2,
                      mb: 1,
                      background: sample.priorite === Priority.URGENTE 
                        ? `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)}, ${alpha(theme.palette.error.main, 0.05)})`
                        : index % 2 === 0
                          ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.info.main, 0.02)})`
                          : `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.02)}, ${alpha(theme.palette.warning.main, 0.02)})`,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateX(8px)',
                        boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.2)}`,
                        borderColor: alpha(theme.palette.primary.main, 0.4),
                      }
                    }}
                  >
                    <ListItemIcon>
                      <Badge 
                        badgeContent={index + 1}
                        color="primary"
                        sx={{
                          '& .MuiBadge-badge': {
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
                          }
                        }}
                      >
                        <Box sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.2rem',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                        }}>
                          🧪
                        </Box>
                      </Badge>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1" component="span" sx={{ fontWeight: 'bold' }}>
                            {sample.sample_id}
                          </Typography>
                          <Chip
                            label={sample.priorite}
                            color={getPriorityColor(sample.priorite)}
                            size="small"
                          />
                          {analysisReadings.length > 0 && (
                          <Chip
                              label={`${analysisReadings.length} éléments`}
                              color="success"
                            variant="outlined"
                            size="small"
                          />
                          )}
                        </Box>
                      }
                      secondary={
                          <Typography variant="body2" color="text.secondary">
                            Client: {sample.client?.company_name || 'N/A'} | 
                            Réf: {sample.external_id || 'N/A'} |
                          Reçu: {sample.date_reception ? new Date(sample.date_reception).toLocaleDateString() : 'N/A'} |
                          Poids: {preparationData?.initial_weight || 'N/A'}g |
                          Méthode: {preparationData?.digestion_method || 'N/A'}
                          </Typography>
                      }
                    />
                    <Box display="flex" gap={1}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => handleStartAnalysis(sample)}
                        color={analysisReadings.length > 0 ? 'success' : 'secondary'}
                      >
                        {analysisReadings.length > 0 ? 'Finaliser' : 'Commencer'}
                      </Button>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Analysis Dialog */}
      <Dialog open={analysisDialog} onClose={() => setAnalysisDialog(false)} maxWidth="xl" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
              <AnalyticsIcon color="primary" />
              <Box>
            <Typography variant="h6">
                  Analyse ICP - {analysisData.sample_id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {analysisData.preparation_data ? 
                    `Poids: ${analysisData.preparation_data.initial_weight}g | Dilution: ${analysisData.preparation_data.total_dilution_factor}x` : 
                    'Données de préparation non disponibles'
                  }
            </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setAnalysisDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
            <Tab label="Conditions Instrument" icon={<Settings />} />
            <Tab label="Résultats" icon={<BarChart />} />
            <Tab label="Validation Méthode" icon={<VerifiedUser />} />
            <Tab label="Contrôle Qualité" icon={<AssessmentIcon />} />
          </Tabs>

          {activeTab === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>Conditions Analytiques ICP</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                    label="Puissance RF (kW)"
                    value={analysisData.instrument_status.r_squared}
                    size="small"
                    InputProps={{ readOnly: true }}
                />
              </Grid>
                <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                    label="Heures Torche"
                    value={analysisData.instrument_status.torch_hours}
                    size="small"
                    InputProps={{ readOnly: true }}
                />
              </Grid>
                <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                    label="Température (°C)"
                    value={analysisData.instrument_status.temperature.toFixed(1)}
                    size="small"
                    InputProps={{ readOnly: true }}
                />
              </Grid>
                <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                    label="Humidité (%)"
                    value={analysisData.instrument_status.humidity.toFixed(1)}
                    size="small"
                    InputProps={{ readOnly: true }}
                />
              </Grid>
              </Grid>
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Typography variant="h6">Résultats d'analyse ({analysisData.analysis_readings.length} éléments)</Typography>
                <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                    startIcon={<DownloadIcon />}
                    size="small"
                  >
                    Import CSV
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    size="small"
                >
                  Ajouter
                </Button>
                </Box>
              </Box>

              {analysisData.analysis_readings.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Élément</TableCell>
                        <TableCell>Intensité</TableCell>
                        <TableCell>Conc. (ppm)</TableCell>
                        <TableCell>Conc. finale (%)</TableCell>
                        <TableCell>Incertitude</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analysisData.analysis_readings.map((reading) => (
                        <TableRow key={reading.id}>
                          <TableCell>{reading.element}</TableCell>
                          <TableCell>{reading.intensity.toFixed(0)}</TableCell>
                          <TableCell>{formatWithDetectionLimit(reading.element, reading.concentration_ppm)}</TableCell>
                          <TableCell>{reading.concentration_final_percent.toFixed(4)}</TableCell>
                          <TableCell>{reading.uncertainty.toFixed(3)}</TableCell>
                          <TableCell>
                            <IconButton size="small" color="primary">
                              <Edit />
                            </IconButton>
                            <IconButton size="small" color="error">
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Aucun résultat d'analyse. Utilisez l'import CSV ou ajoutez manuellement les résultats.
                </Alert>
              )}
            </Box>
          )}

          {activeTab === 2 && (
              <Box>
              <Typography variant="h6" gutterBottom>Validation des Méthodes ICP</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Élément</TableCell>
                      <TableCell>LOD (ppm)</TableCell>
                      <TableCell>LOQ (ppm)</TableCell>
                      <TableCell>Exactitude (%)</TableCell>
                      <TableCell>Précision RSD (%)</TableCell>
                      <TableCell>Incertitude (%)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {methodValidations.map((validation) => (
                      <TableRow key={validation.element}>
                        <TableCell>{validation.element}</TableCell>
                        <TableCell>{validation.lod_ppm.toFixed(3)}</TableCell>
                        <TableCell>{validation.loq_ppm.toFixed(3)}</TableCell>
                        <TableCell>{validation.accuracy_percent.toFixed(1)}</TableCell>
                        <TableCell>{validation.precision_rsd_percent.toFixed(1)}</TableCell>
                        <TableCell>{validation.expanded_uncertainty_percent.toFixed(1)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              </Box>
            )}

          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>Contrôle Qualité</Typography>
              <Alert severity="info">
                Les fonctions de contrôle qualité (blancs, standards, duplicatas) seront disponibles prochainement.
              </Alert>
            </Box>
          )}

          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              label="Notes d'analyse"
              multiline
              rows={3}
              value={analysisData.notes}
              onChange={(e) => setAnalysisData({ ...analysisData, notes: e.target.value })}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setAnalysisDialog(false)}>
            Annuler
          </Button>
          <Button 
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            disabled={analysisData.analysis_readings.length === 0}
            onClick={handleCompleteAnalysis}
          >
            Terminer analyse
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Analysis Dialog - Enhanced with Import Functionality */}
      <Dialog 
        open={batchAnalysisDialog} 
        onClose={handleCloseBatchDialog} 
        maxWidth={false}
        fullScreen
        PaperProps={{ 
          sx: { 
            backgroundColor: '#f8f9fa',
            '& .MuiDialogTitle-root': {
              backgroundColor: '#1976d2',
              color: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 1000
            }
          } 
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <AnalyticsIcon sx={{ fontSize: '1.8rem' }} />
              <Typography variant="h5" fontWeight="bold">
                Analyse ICP - Lot {selectedBatch?.batchNumber}
              </Typography>
              <Chip 
                label={`${selectedBatch?.samples.length || 0} échantillons`} 
                color="secondary" 
                variant="filled"
                sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}
              />
              {/* ✅ REMOVED: batchInitializing chip - initialization is now instant */}
            </Box>
            <Button
              onClick={handleCloseBatchDialog}
              sx={{ color: 'white', minWidth: 'auto', p: 1 }}
            >
              ✕
            </Button>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, height: '100vh', overflow: 'hidden' }}>
          {/* ✅ REMOVED: batchInitializing loading state - initialization is now instant */}
          <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
            {/* Batch Analysis Results Entry */}
            <Card>
              <CardContent sx={{ p: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                    📊 Saisie des Résultats ICP - Lot {selectedBatch?.batchNumber}
                  </Typography>
                  
                    {/* NEW: Import Button */}
                    <Box display="flex" gap={2} alignItems="center">
                      <Button
                        variant="contained"
                        startIcon={importLoading ? <CircularProgress size={16} /> : <FolderOpen />}
                        onClick={scanICPDirectory}
                        disabled={importLoading}
                        sx={{
                          background: 'linear-gradient(45deg, #FF6B6B, #FF8E53)',
                          color: 'white',
                          fontWeight: 'bold',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #FF5252, #FF7043)',
                          }
                        }}
                      >
                        {importLoading ? 'Recherche...' : 'Import CSV'}
                      </Button>
                  
                    {/* Elements Display */}
                      <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                        {getBatchElementOrder().map((element: string) => (
                        <Chip 
                          key={element} 
                          label={element} 
                          size="small"
                          color="primary" 
                          variant="outlined"
                          sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                        />
                      ))}
                        
                        {/* Quick Fill Button */}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            if (selectedBatch) {
                              const updatedData = { ...batchAnalysisData };
                              
                              selectedBatch.samples.forEach(sample => {
                                if (!updatedData[sample.id]) updatedData[sample.id] = {};
                                
                                getBatchElementOrder().forEach(element => {
                                  if (!updatedData[sample.id][element] || (updatedData[sample.id][element].ppm ?? 0) < 0) {
                                    updatedData[sample.id][element] = {
                                      ppm: 0,
                                      percent: 0,
                                      timestamp: new Date().toISOString()
                                    };
                                  }
                                });
                              });
                              
                              setBatchAnalysisData(updatedData);
                              console.log('✅ Filled missing elements with 0 for all samples');
                            }
                          }}
                          sx={{ 
                            fontSize: '0.7rem', 
                            minWidth: 'auto',
                            whiteSpace: 'nowrap',
                            backgroundColor: alpha(theme.palette.warning.main, 0.1),
                            borderColor: theme.palette.warning.main,
                            color: theme.palette.warning.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.warning.main, 0.2),
                            }
                          }}
                        >
                          Remplir avec 0
                        </Button>
                      </Box>
                    </Box>
                  </Box>

                  {/* Formula Display */}
                  <Alert severity="info" sx={{ mb: 2, py: 1 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                      💡 Formule ICP: <strong>% = ppm ÷ 10 000</strong> · Éléments majeurs (Fe, Al, Ca, K, Mg, Mn, Na, P, S, Si, Ti et leurs oxydes) saisis en <strong>%</strong>, traces en <strong>ppm</strong> · Utilisez "Import CSV" pour charger automatiquement les données depuis le répertoire ICP
                    </Typography>
                  </Alert>

                  {/* Import Status Alert */}
                  {importedFileName && (
                    <Alert severity="success" sx={{ mb: 2, py: 1 }}>
                      📁 Données importées depuis: {importedFileName} - 
                      Vérifiez les valeurs ci-dessous et cliquez "Terminer l'Analyse" pour sauvegarder définitivement.
                    </Alert>
                  )}

                  {/* Analysis Results Table - Only render when data is ready */}
                  {selectedBatch && Object.keys(batchAnalysisData).length > 0 && (
                    <Box sx={{ position: 'relative' }}>
                      {/* ✅ REMOVED: batchInitializing overlay - initialization is now instant */}
                      
                      {/* 🚀 PROFESSIONAL LIMS: Performance optimization info */}
                      <Box sx={{ mb: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                          🏭 Mode Laboratoire Professionnel - Inputs Natifs Optimisés
                        </Typography>
                        <Typography variant="caption" color="primary">
                          ⚡ Performance: Inputs HTML natifs (10x plus rapides que Material-UI) • 
                          Tous les échantillons visibles simultanément • 
                          Interface optimisée pour laboratoires industriels
                        </Typography>
                      </Box>
                      
                      <TableContainer 
                        component={Paper} 
                        sx={{ 
                          maxHeight: '70vh', 
                          overflow: 'auto',
                          // 🚀 PERFORMANCE: Optimized scrolling for large tables
                          '&::-webkit-scrollbar': {
                            width: '8px',
                            height: '8px',
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f1f1f1',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#c1c1c1',
                            borderRadius: '4px',
                          },
                        }}
                      >
                        <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                              <TableCell sx={{ 
                                fontWeight: 'bold', 
                                minWidth: '120px', 
                                position: 'sticky', 
                                left: 0, 
                                backgroundColor: '#f5f5f5', 
                                zIndex: 2 
                              }}>
                              Échantillon
                            </TableCell>
                              <TableCell sx={{ 
                                fontWeight: 'bold', 
                                minWidth: '80px', 
                                position: 'sticky', 
                                left: 120, 
                                backgroundColor: '#f5f5f5', 
                                zIndex: 2 
                              }}>
                                Priorité
                            </TableCell>
                              {getBatchElementOrder().map((element: string) => (
                                <TableCell key={element} sx={{ 
                                  fontWeight: 'bold', 
                                  minWidth: '85px', 
                                  maxWidth: '85px',
                                  textAlign: 'center',
                                  fontSize: '0.75rem',
                                  padding: '8px 4px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  backgroundColor: '#f5f5f5',
                                  zIndex: 1
                                }}>
                                  {element}
                                  <br />
                                  <span style={{ fontSize: '0.65rem', color: '#666' }}>
                                    ({isPercentDisplayElement(element) ? '%' : 'ppm'})
                                  </span>
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {/* 🚀 PERFORMANCE FIX: Virtual rendering - only show limited samples initially */}
                          {selectedBatch.samples.map((sample: Sample, index: number) => {
                            const sampleAnalysisData = batchAnalysisData[sample.id] || {};
                              // 🔥 CRITICAL FIX: Use consistent batch element order for ALL samples
                              const elementsToAnalyze = getBatchElementOrder();
                              // 🔧 FIX: Get this sample's specific elements for greyed field detection
                              const sampleSpecificElems = getSampleSpecificElements(sample);
                            
                            return (
                                <TableRow key={sample.id} sx={{ '&:nth-of-type(even)': { backgroundColor: '#f8f9fa' } }}>
                                <TableCell sx={{ 
                                  fontWeight: 'bold', 
                                  position: 'sticky', 
                                  left: 0, 
                                    backgroundColor: 'inherit', 
                                  zIndex: 1,
                                    minWidth: '120px'
                                }}>
                                  {sample.sample_id}
                                </TableCell>
                                  <TableCell sx={{ 
                                    position: 'sticky', 
                                    left: 120, 
                                    backgroundColor: 'inherit', 
                                    zIndex: 1,
                                    minWidth: '80px'
                                  }}>
                                  <Chip 
                                      label={sample.priorite} 
                                    size="small" 
                                      color={sample.priorite === Priority.URGENTE ? 'error' : 'default'}
                                      sx={{ fontSize: '0.7rem' }}
                                  />
                                </TableCell>
                                
                                {elementsToAnalyze.map((element: string) => {
                                  // 🔧 FIX: Check if element is in this sample's selected elements
                                  const hasElement = sampleSpecificElems.includes(element);
                                  const elementData = sampleAnalysisData[element];
                                  // Major elements (Fe, Al, Ca, …) are displayed/edited in %; trace elements in ppm.
                                  const displayInPercent = isPercentDisplayElement(element);
                                  const displayValue = displayInPercent
                                    ? (elementData?.percent ?? null)
                                    : (elementData?.ppm ?? null);
                                  const hasValue = displayInPercent
                                    ? (elementData?.percent !== null && elementData?.percent !== undefined)
                                    : (elementData?.ppm !== null && elementData?.ppm !== undefined);
                                  
                                  // 🔧 FIX: Render greyed disabled cell for elements not in this sample
                                  if (!hasElement) {
                                    return (
                                      <TableCell key={`${sample.id}-${element}`} sx={{ 
                                        padding: '4px 2px', 
                                        minWidth: '85px', 
                                        maxWidth: '85px',
                                        backgroundColor: '#f0f0f0'
                                      }}>
                                        <Typography sx={{ color: '#bdbdbd', textAlign: 'center', fontSize: '0.8rem' }}>-</Typography>
                                      </TableCell>
                                    );
                                  }
                                    
                                  return (
                                      <TableCell key={`${sample.id}-${element}`} sx={{ 
                                        padding: '4px 2px', 
                                        minWidth: '85px', 
                                        maxWidth: '85px' 
                                      }}>
                                      <NativeNumberInput
                                          value={displayValue}
                                          onChange={handleNativeInputChange}
                                          sampleId={sample.id}
                                          element={element}
                                          hasValue={hasValue}
                                      />
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                      
                      {/* 🚀 PERFORMANCE: Load More Button for large batches */}
                      {selectedBatch && selectedBatch.samples.length > maxVisibleSamples && !showAllSamples && (
                        <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 1, mt: 1 }}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleLoadMoreSamples}
                            disabled={isLoadingMoreSamples}
                            startIcon={isLoadingMoreSamples ? <CircularProgress size={16} /> : null}
                            sx={{ 
                              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                              boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)',
                              color: 'white',
                              fontWeight: 'bold',
                              px: 3,
                              py: 1
                            }}
                          >
                            {isLoadingMoreSamples ? 'Chargement...' : `🚀 Charger tous les ${selectedBatch.samples.length} échantillons`}
                          </Button>
                          <Typography variant="caption" display="block" sx={{ mt: 1, color: '#666', fontStyle: 'italic' }}>
                            ⚡ Affichage optimisé: {getVirtualizedSamples().length} sur {selectedBatch.samples.length} échantillons 
                            • Réduction de {Math.round((1 - getVirtualizedSamples().length / selectedBatch.samples.length) * 100)}% des champs à rendre
                          </Typography>
                        </Box>
                      )}
                      
                      {/* 🚀 PERFORMANCE INFO: Display optimization status */}
                      <Box sx={{ mt: 1, p: 1, bgcolor: showAllSamples ? '#e8f5e8' : '#e3f2fd', borderRadius: 1 }}>
                        <Typography variant="caption" color={showAllSamples ? 'success.main' : 'primary'}>
                          ⚡ Performance: {getVirtualizedSamples().length} échantillons × {getBatchElementOrder().length} éléments = {getVirtualizedSamples().length * getBatchElementOrder().length} cellules
                          {!showAllSamples && ` (${Math.round((1 - getVirtualizedSamples().length / selectedBatch.samples.length) * 100)}% réduction)`}
                          {showAllSamples && ' (TOUS CHARGÉS)'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </CardContent>
            </Card>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, backgroundColor: '#f8f9fa', borderTop: '1px solid #ddd' }}>
          <Button onClick={handleCloseBatchDialog} size="large">
            Annuler
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            size="large"
            onClick={() => {
              if (selectedBatch) {
                let completedCount = 0;
                let totalRequired = 0;
                const missingData: Array<{sample: string, element: string}> = [];
                
                selectedBatch.samples.forEach(sample => {
                  const sampleAnalysisData = batchAnalysisData[sample.id] || {};
                  getBatchElementOrder().forEach((element: string) => {
                    totalRequired++;
                    const elementData = sampleAnalysisData[element];
                    // ✅ CRITICAL FIX: Accept 0 as a valid value (for elements not in the CSV)
                    if (elementData && elementData.ppm >= 0) {
                      completedCount++;
                    } else {
                      missingData.push({sample: sample.sample_id, element});
                    }
                  });
                });
                
                if (completedCount < totalRequired) {
                  console.log('🔍 MISSING DATA DEBUG:', missingData);
                  const missingByElement = missingData.reduce((acc, item) => {
                    if (!acc[item.element]) acc[item.element] = [];
                    acc[item.element].push(item.sample);
                    return acc;
                  }, {} as Record<string, string[]>);
                  
                  const missingElements = Object.keys(missingByElement);
                  const detailedMessage = `Mesures manquantes:\n\n${missingElements.slice(0, 5).map(el => 
                    `• ${el}: ${missingByElement[el].length} échantillon(s) (${missingByElement[el].slice(0, 2).join(', ')}${missingByElement[el].length > 2 ? '...' : ''})`
                  ).join('\n')}${missingElements.length > 5 ? `\n\n... et ${missingElements.length - 5} autres éléments` : ''}`;
                  
                  alert(`Veuillez compléter toutes les mesures ppm.\n\n📊 État: ${completedCount}/${totalRequired} mesures saisies\n\n${detailedMessage}\n\n💡 Astuce: Utilisez "Import CSV" pour remplir automatiquement les données, ou saisissez "0" pour les éléments non détectés.`);
                  return;
                }
                
                handleCompleteBatchAnalysis();
              }
            }}
            startIcon={<AnalyticsIcon />}
            disabled={(() => {
              if (!selectedBatch || Object.keys(batchAnalysisData).length === 0) return true;
              
              let completedCount = 0;
              let totalRequired = 0;
              
              selectedBatch.samples.forEach(sample => {
                const sampleAnalysisData = batchAnalysisData[sample.id] || {};
                getBatchElementOrder().forEach((element: string) => {
                  totalRequired++;
                  const elementData = sampleAnalysisData[element];
                  // 🔬 FIXED: Accept ANY numeric value (including negative values below detection limit)
                  if (elementData && typeof elementData.ppm === 'number' && !isNaN(elementData.ppm)) {
                    completedCount++;
                  }
                });
              });
              
              return completedCount < totalRequired;
            })()}
            sx={{ fontSize: '1rem', px: 3 }}
          >
            {/* ✅ REMOVED: batchInitializing check - initialization is now instant */}
            <>
              🧪 Finaliser Analyse du Lot ({(() => {
                  if (!selectedBatch || Object.keys(batchAnalysisData).length === 0) return '0/0';
                  
                  let completedCount = 0;
                  let totalRequired = 0;
                  
                  selectedBatch.samples.forEach(sample => {
                    const sampleAnalysisData = batchAnalysisData[sample.id] || {};
                    getBatchElementOrder().forEach((element: string) => {
                      totalRequired++;
                      const elementData = sampleAnalysisData[element];
                      // 🔬 FIXED: Accept ANY numeric value (including negative values below detection limit)
                      if (elementData && typeof elementData.ppm === 'number' && !isNaN(elementData.ppm)) {
                        completedCount++;
                      }
                    });
                  });
                  
                  return `${completedCount}/${totalRequired}`;
                })()})
              </>
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import File Selection Dialog */}
      <Dialog open={importDialogOpen} onClose={() => { setImportDialogOpen(false); setSelectedFilesForImport(new Set()); }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <FolderOpen color="primary" />
            <Typography variant="h6">Fichiers ICP Disponibles</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Alert severity="info">
              Sélectionnez un ou plusieurs fichiers pour le lot {selectedBatch?.batchNumber}.
              Pour combiner 4-Acides + Fusion, cochez les deux fichiers puis cliquez sur "Importer la sélection".
            </Alert>
          </Box>
          
          {availableFiles.length > 0 ? (
            <List>
              {availableFiles.map((file, index) => {
                const isSelected = selectedFilesForImport.has(file.filename);
                const typeColor = file.analysisType === 'fusion' ? 'secondary' : file.analysisType === '4-acides' ? 'warning' : 'default';
                const typeLabel = file.analysisType === 'fusion' ? 'Fusion' : file.analysisType === '4-acides' ? '4-Acides' : 'Inconnu';
                
                return (
                  <ListItem 
                    key={index} 
                    sx={{ 
                      border: isSelected ? '2px solid' : '1px solid #e0e0e0', 
                      borderColor: isSelected ? 'primary.main' : '#e0e0e0',
                      borderRadius: 2, 
                      mb: 1,
                      bgcolor: isSelected ? 'action.selected' : 'transparent',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      const next = new Set(selectedFilesForImport);
                      if (next.has(file.filename)) next.delete(file.filename);
                      else next.add(file.filename);
                      setSelectedFilesForImport(next);
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox 
                        checked={isSelected} 
                        disabled={file.matchedSamples.length === 0}
                        tabIndex={-1}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {file.filename}
                          </Typography>
                          <Chip label={typeLabel} size="small" color={typeColor as any} />
                          {file.matchedSamples.length > 0 && (
                            <Chip 
                              label={`${file.matchedSamples.length} match`}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          <span style={{ display: 'block', fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                            Taille: {(file.size / 1024).toFixed(1)} KB | Modifié: {new Date(file.modified).toLocaleString()}
                          </span>
                          {file.matchedSamples.length > 0 && (
                            <span style={{ display: 'block', marginTop: '4px', fontSize: '0.75rem' }}>
                              {file.matchedSamples.join(', ')}
                            </span>
                          )}
                        </>
                      }
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleImportICPFile(file.filename); }}
                      disabled={file.matchedSamples.length === 0 || importLoading}
                      startIcon={importLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
                    >
                      {importLoading ? 'Import...' : 'Seul'}
                    </Button>
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <Alert severity="warning">
              Aucun fichier ICP trouvé dans le répertoire.
            </Alert>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => { setImportDialogOpen(false); setSelectedFilesForImport(new Set()); }}>
            Fermer
          </Button>
          <Button 
            variant="outlined" 
            onClick={scanICPDirectory}
            startIcon={<RefreshIcon />}
            disabled={importLoading}
          >
            Actualiser
          </Button>
          {selectedFilesForImport.size > 1 && (
            <Button 
              variant="contained"
              color="primary"
              onClick={handleMultiFileImport}
              disabled={importLoading}
              startIcon={importLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
            >
              {importLoading ? 'Import...' : `Importer la sélection (${selectedFilesForImport.size} fichiers)`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
});

export default ICPAnalystDashboard; 